import { SITE_NAME } from "@/constants/seo";

// Power words for CTR optimization
const POWER_WORDS = {
  trust: ["Verificado", "Certificado", "Oficial", "Profesional", "Autorizado"],
  urgency: ["Ahora", "Hoy", "2025", "Actualizado"],
  value: ["Mejor", "Top", "Premium", "Exclusivo", "Recomendado"],
  action: ["Descubre", "Encuentra", "Compara", "Reserva"],
  social: ["Opiniones Reales", "Valorado", "Popular", "Favorito"],
};

// Get current year
const CURRENT_YEAR = new Date().getFullYear();

// Hash function for consistent rotation
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

// Truncate to max length with ellipsis
const truncateTitle = (title: string, maxLength: number = 60): string => {
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength - 3).trim() + "...";
};

// ============================================
// LISTING TITLES
// ============================================

interface ListingTitleParams {
  name: string;
  city: string;
  type: string;
  rating?: number;
  isPremium?: boolean;
  tags?: string[];
}

const LISTING_TITLE_PATTERNS = [
  (p: ListingTitleParams) => `${p.name} ${p.city} - ${p.type} TOP ${CURRENT_YEAR}`,
  (p: ListingTitleParams) => `${p.name} en ${p.city} | ${p.type} Verificado`,
  (p: ListingTitleParams) => `Mejor ${p.type} ${p.city}: ${p.name}`,
  (p: ListingTitleParams) => `${p.name} ${p.city} - Precios y Horarios ${CURRENT_YEAR}`,
  (p: ListingTitleParams) => `${p.type} ${p.name} ${p.city} | Opiniones Reales`,
  (p: ListingTitleParams) => `${p.name} - ${p.type} Recomendado en ${p.city}`,
  (p: ListingTitleParams) => `Top ${p.type} ${p.city}: ${p.name} ${CURRENT_YEAR}`,
  (p: ListingTitleParams) => `${p.name} ${p.city} | ${p.type} Premium`,
];

const PREMIUM_LISTING_PATTERNS = [
  (p: ListingTitleParams) => `⭐ ${p.name} ${p.city} - ${p.type} Premium`,
  (p: ListingTitleParams) => `${p.name} ${p.city} | ${p.type} Destacado ${CURRENT_YEAR}`,
  (p: ListingTitleParams) => `Premium: ${p.name} - Mejor ${p.type} ${p.city}`,
];

export const generateListingTitle = (params: ListingTitleParams): string => {
  const patterns = params.isPremium ? PREMIUM_LISTING_PATTERNS : LISTING_TITLE_PATTERNS;
  const hash = hashString(`${params.name}-${params.city}-${params.type}`);
  const patternIndex = hash % patterns.length;
  const title = patterns[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// ARTICLE TITLES
// ============================================

interface ArticleTitleParams {
  title: string;
  category: string;
  readingTime?: number;
}

const ARTICLE_TITLE_PATTERNS = [
  (p: ArticleTitleParams) => `${p.title} | Guía ${CURRENT_YEAR}`,
  (p: ArticleTitleParams) => `${p.title} - ${p.category} Profesional`,
  (p: ArticleTitleParams) => `${p.title} | Consejos de Expertos`,
  (p: ArticleTitleParams) => `${p.title} - Guía Completa ${CURRENT_YEAR}`,
  (p: ArticleTitleParams) => `${p.title} | ${p.category} Actualizado`,
];

export const generateArticleTitle = (params: ArticleTitleParams): string => {
  const hash = hashString(params.title);
  const patternIndex = hash % ARTICLE_TITLE_PATTERNS.length;
  const title = ARTICLE_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// TRAINER TITLES
// ============================================

interface TrainerTitleParams {
  name: string;
  city: string;
  specialty?: string;
  yearsExperience?: number;
}

const TRAINER_TITLE_PATTERNS = [
  (p: TrainerTitleParams) => `${p.name} - Entrenador Personal ${p.city} ${CURRENT_YEAR}`,
  (p: TrainerTitleParams) => `${p.name} ${p.city} | Entrenador Certificado`,
  (p: TrainerTitleParams) => `Entrenador Personal ${p.name} en ${p.city}`,
  (p: TrainerTitleParams) => `${p.name} - Personal Trainer ${p.city} Verificado`,
  (p: TrainerTitleParams) => `${p.name} ${p.city} | Experto Fitness ${CURRENT_YEAR}`,
];

const SPECIALTY_TRAINER_PATTERNS = [
  (p: TrainerTitleParams) => `${p.name} - ${p.specialty} ${p.city} ${CURRENT_YEAR}`,
  (p: TrainerTitleParams) => `${p.name} ${p.city} | Especialista en ${p.specialty}`,
  (p: TrainerTitleParams) => `Entrenador ${p.specialty}: ${p.name} en ${p.city}`,
];

export const generateTrainerTitle = (params: TrainerTitleParams): string => {
  const patterns = params.specialty ? SPECIALTY_TRAINER_PATTERNS : TRAINER_TITLE_PATTERNS;
  const hash = hashString(`${params.name}-${params.city}`);
  const patternIndex = hash % patterns.length;
  const title = patterns[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// EVENT TITLES
// ============================================

interface EventTitleParams {
  title: string;
  city?: string;
  date?: string;
  eventType: string;
  price?: number;
}

const EVENT_TITLE_PATTERNS = [
  (p: EventTitleParams) => `${p.title} ${p.city || ""} ${CURRENT_YEAR}`.trim(),
  (p: EventTitleParams) => `${p.title} | ${p.eventType} ${p.city || ""}`.trim(),
  (p: EventTitleParams) => `${p.eventType}: ${p.title} en ${p.city || "España"}`,
  (p: EventTitleParams) => `${p.title} - ${p.eventType} Deportivo ${CURRENT_YEAR}`,
];

export const generateEventTitle = (params: EventTitleParams): string => {
  const hash = hashString(params.title);
  const patternIndex = hash % EVENT_TITLE_PATTERNS.length;
  const title = EVENT_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// VIDEO TITLES
// ============================================

interface VideoTitleParams {
  title: string;
  category?: string;
  tags?: string[];
}

const VIDEO_TITLE_PATTERNS = [
  (p: VideoTitleParams) => `${p.title} | Tutorial ${p.category || "Fitness"}`,
  (p: VideoTitleParams) => `${p.title} - Video ${p.category || "Deportivo"} ${CURRENT_YEAR}`,
  (p: VideoTitleParams) => `${p.title} | Guía Visual ${CURRENT_YEAR}`,
  (p: VideoTitleParams) => `Tutorial: ${p.title} | ${p.category || "Fitness"}`,
];

export const generateVideoTitle = (params: VideoTitleParams): string => {
  const hash = hashString(params.title);
  const patternIndex = hash % VIDEO_TITLE_PATTERNS.length;
  const title = VIDEO_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// NUTRITIONIST TITLES
// ============================================

interface NutritionistTitleParams {
  name: string;
  city: string;
  specialty?: string;
}

const NUTRITIONIST_TITLE_PATTERNS = [
  (p: NutritionistTitleParams) => `${p.name} - Nutricionista ${p.city} ${CURRENT_YEAR}`,
  (p: NutritionistTitleParams) => `${p.name} ${p.city} | Nutricionista Certificado`,
  (p: NutritionistTitleParams) => `Nutricionista ${p.name} en ${p.city} | Experto`,
  (p: NutritionistTitleParams) => `${p.name} - Dietista Profesional ${p.city}`,
  (p: NutritionistTitleParams) => `${p.name} ${p.city} | Nutrición Deportiva`,
];

export const generateNutritionistTitle = (params: NutritionistTitleParams): string => {
  const hash = hashString(`${params.name}-${params.city}`);
  const patternIndex = hash % NUTRITIONIST_TITLE_PATTERNS.length;
  const title = NUTRITIONIST_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// PHYSIOTHERAPIST TITLES
// ============================================

interface PhysiotherapistTitleParams {
  name: string;
  city: string;
  specialty?: string;
}

const PHYSIO_TITLE_PATTERNS = [
  (p: PhysiotherapistTitleParams) => `${p.name} - Fisioterapeuta ${p.city} ${CURRENT_YEAR}`,
  (p: PhysiotherapistTitleParams) => `${p.name} ${p.city} | Fisioterapeuta Deportivo`,
  (p: PhysiotherapistTitleParams) => `Fisioterapia ${p.name} en ${p.city} | Experto`,
  (p: PhysiotherapistTitleParams) => `${p.name} - Fisio Profesional ${p.city}`,
  (p: PhysiotherapistTitleParams) => `${p.name} ${p.city} | Rehabilitación Deportiva`,
];

export const generatePhysiotherapistTitle = (params: PhysiotherapistTitleParams): string => {
  const hash = hashString(`${params.name}-${params.city}`);
  const patternIndex = hash % PHYSIO_TITLE_PATTERNS.length;
  const title = PHYSIO_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};

// ============================================
// DIRECTORY TITLES
// ============================================

interface DirectoryTitleParams {
  type?: string;
  city?: string;
  activity?: string;
  count?: number;
}

export const generateDirectoryTitle = (params: DirectoryTitleParams): string => {
  const { type, city, activity, count } = params;
  
  if (activity && city) {
    return truncateTitle(`${activity} en ${city} - Top ${count || 10} Mejores ${CURRENT_YEAR}`);
  }
  
  if (city) {
    return truncateTitle(`Gimnasios y Centros Fitness ${city} ${CURRENT_YEAR}`);
  }
  
  if (type) {
    return truncateTitle(`Mejores ${type} España - Directorio ${CURRENT_YEAR}`);
  }
  
  return truncateTitle(`Directorio Fitness España - Gimnasios y Centros ${CURRENT_YEAR}`);
};

// ============================================
// LANDING PAGE TITLES (Unique per page)
// ============================================

export const LANDING_TITLES: Record<string, string> = {
  fitness: `Marketing para Gimnasios España ${CURRENT_YEAR} | Más Clientes`,
  crossfit: `Promoción Boxes CrossFit ${CURRENT_YEAR} | Atrae Atletas`,
  yoga: `Marketing Estudios Yoga ${CURRENT_YEAR} | Más Alumnos`,
  pilates: `Promoción Estudios Pilates ${CURRENT_YEAR} | Crece Tu Centro`,
  bienestar: `Marketing Centros Bienestar ${CURRENT_YEAR} | Más Visibilidad`,
  losMejores: `Top Profesionales Deportivos España ${CURRENT_YEAR} | Rankings`,
  entrenadores: `Entrenadores Personales España ${CURRENT_YEAR} | Directorio`,
  nutricionistas: `Nutricionistas Deportivos España ${CURRENT_YEAR} | Expertos`,
  fisioterapeutas: `Fisioterapeutas Deportivos España ${CURRENT_YEAR} | Top`,
  eventos: `Eventos Deportivos España ${CURRENT_YEAR} | Calendario`,
  videos: `Videos Fitness y Tutoriales ${CURRENT_YEAR} | Aprende`,
  articulos: `Blog Fitness y Nutrición ${CURRENT_YEAR} | Consejos Expertos`,
  tiendas: `Tiendas Deportivas España ${CURRENT_YEAR} | Directorio`,
  'artes-marciales': `Academias Artes Marciales ${CURRENT_YEAR} | Más Alumnos`,
  baile: `Academias de Baile España ${CURRENT_YEAR} | Más Alumnos`,
  'centros-bienestar': `Centros Bienestar España ${CURRENT_YEAR} | Sin Comisiones`,
};

// Generate landing page title from type
export const generateLandingPageTitle = (type: string): string => {
  return LANDING_TITLES[type] || `${type.charAt(0).toUpperCase() + type.slice(1)} España ${CURRENT_YEAR} | Ekselencia`;
};

// ============================================
// SPORT PROFESSIONAL TITLES
// ============================================

interface SportProfessionalTitleParams {
  name: string;
  specialty: string;
  city?: string;
  country?: string;
}

const SPORT_PRO_TITLE_PATTERNS = [
  (p: SportProfessionalTitleParams) => `${p.name} - ${p.specialty} ${p.city || p.country || ""} ${CURRENT_YEAR}`.trim(),
  (p: SportProfessionalTitleParams) => `${p.name} | ${p.specialty} Profesional`,
  (p: SportProfessionalTitleParams) => `${p.specialty}: ${p.name} - Perfil Oficial`,
  (p: SportProfessionalTitleParams) => `${p.name} ${p.city || ""} | Top ${p.specialty}`.trim(),
];

export const generateSportProfessionalTitle = (params: SportProfessionalTitleParams): string => {
  const hash = hashString(`${params.name}-${params.specialty}`);
  const patternIndex = hash % SPORT_PRO_TITLE_PATTERNS.length;
  const title = SPORT_PRO_TITLE_PATTERNS[patternIndex](params);
  return truncateTitle(title);
};
